import { ethers } from "ethers";
import { __DEBUG__, __STAGING__ } from "./dev";
import { NETWORK_TYPE } from "../constants/network";

const MATIC = {
  name: "Matic",
  symbol: "MATIC",
  decimals: 18
};

const BNB = {
  name: "Binance Coin",
  symbol: "BNB",
  decimals: 18
};

const ETH = {
  name: "Etherum",
  symbol: "ETH",
  decimals: 18
};

const AETH = {
  name: "AEtherum",
  symbol: "AETH",
  decimals: 18
};

const ALCHEMY_API_KEY = process.env.REACT_APP_ALCHEMY_API_KEY;
const RPC_FALLBACK_KEY_PREFIX = "dm_rpc_fallback_index_";

const BSC_MAINNET_RPCS = [
  "https://bsc-dataseed1.bnbchain.org",
  "https://bsc-dataseed2.bnbchain.org",
  "https://bsc-dataseed3.bnbchain.org",
  "https://bsc-dataseed.bnbchain.org",
  "https://rpc.ankr.com/bsc/5831c7a9de6909397d9982ceb0531723888e3a9921869221a47d1116fcaf2550"
];

const BSC_TESTNET_RPCS = [
  "https://data-seed-prebsc-1-s1.binance.org:8545",
  "https://data-seed-prebsc-2-s1.binance.org:8545"
];

export const CHAINS = {
  1: {
    id: 1,
    rpc: `https://eth-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`,
    rpcUrls: [`https://eth-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`],
    name: "Etherum Mainnet",
    nativeCurrency: ETH,
    blockExplorerUrls: ["https://etherscan.io/"]
  },
  5: {
    id: 5,
    rpc: `https://eth-goerli.g.alchemy.com/v2/${ALCHEMY_API_KEY}`,
    rpcUrls: [`https://eth-goerli.g.alchemy.com/v2/${ALCHEMY_API_KEY}`],
    name: "Goerli Test Network",
    nativeCurrency: ETH,
    blockExplorerUrls: ["https://goerli.etherscan.io/"]
  },
  11155111: {
    id: 11155111,
    rpc: `https://eth-sepolia.g.alchemy.com/v2/${ALCHEMY_API_KEY}`,
    rpcUrls: [`https://eth-sepolia.g.alchemy.com/v2/${ALCHEMY_API_KEY}`],
    name: "Sepolia Test Netwok",
    nativeCurrency: ETH,
    blockExplorerUrls: ["https://sepolia.etherscan.io/"]
  },
  137: {
    id: 137,
    rpc: `https://polygon-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`,
    rpcUrls: [`https://polygon-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`],
    name: "Polygon Mainnet",
    nativeCurrency: MATIC,
    blockExplorerUrls: ["https://polygonscan.com"]
  },
  80001: {
    id: 80001,
    rpc: `https://polygon-mumbai.g.alchemy.com/v2/${ALCHEMY_API_KEY}`,
    rpcUrls: [`https://polygon-mumbai.g.alchemy.com/v2/${ALCHEMY_API_KEY}`],
    name: "Polygon Mumbai",
    nativeCurrency: MATIC,
    blockExplorerUrls: ["https://mumbai.polygonscan.com"]
  },
  42161: {
    id: 42161,
    rpc: `https://arb-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`,
    rpcUrls: [`https://arb-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`],
    name: "Arbitrum Mainnet",
    nativeCurrency: AETH,
    blockExplorerUrls: ["https://arbiscan.io/"]
  },
  10: {
    id: 42161,
    rpc: `https://opt-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`,
    rpcUrls: [`https://opt-mainnet.g.alchemy.com/v2/${ALCHEMY_API_KEY}`],
    name: "Optimism Mainnet",
    nativeCurrency: ETH,
    blockExplorerUrls: ["https://optimistic.etherscan.io"]
  },
  56: {
    id: 56,
    rpc: BSC_MAINNET_RPCS[0],
    rpcUrls: BSC_MAINNET_RPCS,
    name: "Binance Smart Chain Mainnet",
    nativeCurrency: BNB,
    blockExplorerUrls: ["https://bscscan.com"]
  },
  97: {
    id: 97,
    rpc: BSC_TESTNET_RPCS[0],
    rpcUrls: BSC_TESTNET_RPCS,
    name: "BSC Testnet",
    nativeCurrency: BNB,
    blockExplorerUrls: ["https://explorer.binance.org/smart-testnet"]
  }
};

// export const DMA_CORRECT_CHAIN = __STAGING__ || __DEBUG__ ? CHAINS[5] : CHAINS[1];
export const CORRECT_CHAIN = __STAGING__ || __DEBUG__ ? CHAINS[11155111] : CHAINS[1];

export const getCorrectChain = (type) => {
  if (type === NETWORK_TYPE.BSC) {
    return __STAGING__ || __DEBUG__ ? CHAINS[97] : CHAINS[56];
  }
  return CORRECT_CHAIN;
};

const isWalletExtensionConflict = (error) => {
  return error.message.includes("is not supported");
};

const dedupeRpcUrls = (rpcUrls = []) => {
  return Array.from(new Set(rpcUrls.filter(Boolean)));
};

export const getChainRpcUrls = (chain) => {
  const rpcUrls = dedupeRpcUrls(chain?.rpcUrls?.length ? chain.rpcUrls : [chain?.rpc]);
  if (!rpcUrls.length || typeof window === "undefined" || !window.localStorage) return rpcUrls;

  const key = `${RPC_FALLBACK_KEY_PREFIX}${chain.id}`;
  const index = Number(window.localStorage.getItem(key));
  if (!Number.isInteger(index) || index < 0 || index >= rpcUrls.length) return rpcUrls;

  return [...rpcUrls.slice(index), ...rpcUrls.slice(0, index)];
};

export const isRpcEndpointOverloadedError = (error) => {
  const message = [
    error?.message,
    error?.reason,
    error?.error?.message,
    error?.data?.message
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

  return (
    message.includes("rpc endpoint returned too many errors") ||
    message.includes("too many requests") ||
    message.includes("status code 429")
  );
};

export const rotateChainRpc = async (chain) => {
  if (typeof window === "undefined") return false;
  const { ethereum } = window;
  const rpcUrls = dedupeRpcUrls(chain?.rpcUrls?.length ? chain.rpcUrls : [chain?.rpc]);
  if (!ethereum || rpcUrls.length < 2) return false;

  const key = `${RPC_FALLBACK_KEY_PREFIX}${chain.id}`;
  const currentIndex = Number(window.localStorage.getItem(key));
  const nextIndex = Number.isInteger(currentIndex) && currentIndex >= 0
    ? (currentIndex + 1) % rpcUrls.length
    : 1 % rpcUrls.length;

  window.localStorage.setItem(key, String(nextIndex));

  try {
    await ethereum.request({
      method: "wallet_addEthereumChain",
      params: [
        {
          chainName: chain.name,
          chainId: ethers.utils.hexValue(chain.id),
          nativeCurrency: chain.nativeCurrency,
          rpcUrls: getChainRpcUrls(chain),
          blockExplorerUrls: chain.blockExplorerUrls
        }
      ]
    });
    return true;
  } catch (error) {
    return false;
  }
};

export const changeNetwork = async (chain) => {
  const { ethereum } = window;
  if (ethereum?.networkVersion !== String(chain.id)) {
    const hexChain = ethers.utils.hexValue(chain.id);

    try {
      await ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: hexChain }]
      });
      console.log("ETH", ethereum);
      return true;
    } catch (err) {
      console.log(err);
      if (isWalletExtensionConflict(err)) {
        return false;
      }
      // This error code indicates that the chain has not been added to MetaMask.
      if (err.code === 4902) {
        return await window.ethereum.request({
          method: "wallet_addEthereumChain",
          params: [
            {
              chainName: chain.name,
              chainId: hexChain,
              nativeCurrency: chain.nativeCurrency,
              rpcUrls: getChainRpcUrls(chain),
              blockExplorerUrls: chain.blockExplorerUrls
            }
          ]
        });
      } else return false;
    }
  }
};
